import { Typography } from "@/components/atoms";
import { NotEditableReason } from "@/redux/apis/clientBooking/types";
import { clsx } from "@/utils";

interface BookingSheetBannerProps {
  /** `view` = read-only booking; `edit` = editing an existing booking. */
  mode: "view" | "edit";
  /** Display job id, shown in the edit copy ("Editing booking J000123"). */
  jobLabel?: string;
  /** Why the booking is locked. Null falls back to wording true of every reason — see VIEW_COPY. */
  notEditableReason?: NotEditableReason | null;
  className?: string;
}

/**
 * Wording true of EVERY locked booking, used when the reason is missing — the trailing clause alone,
 * since the shape's first half names what happened and here we do not know what happened.
 *
 * Missing is a real case, not a defensive one: a frontend deployed ahead of the backend gets no
 * reason at all, and an unrecognised value (a reason added server-side later) lands here too. It has
 * to be honest on its own rather than a placeholder — which is exactly what the old single message
 * was not.
 */
const VIEW_FALLBACK = "This booking is now view-only";

/**
 * One line per reason, keeping the original banner's shape — "{what happened} and is now view-only",
 * so only the first clause varies. The single message this replaced IS the `closed` line, verbatim.
 *
 * Only `closed` and `cancelled` describe a finished job; the rest are live bookings past the point of
 * changing, and the whole point of this map is that they stop being called closed. A driver already on
 * the way is the case that prompted it — a client waiting at the kerb was being told it was closed.
 */
const VIEW_COPY: Record<NotEditableReason, string> = {
  closed: "This booking is closed and is now view-only",
  cancelled: "This booking was cancelled and is now view-only",
  in_progress: "This trip is under way and is now view-only",
  too_close_to_pickup: "This booking is too close to pickup and is now view-only",
  paid: "This booking has been paid and is now view-only",
  special_event: "This booking can't be changed online and is now view-only",
};

/**
 * Full-width status bar atop the Booking Sheet / Summary, telling the user what state the sheet is in:
 *  - **view** — silver-gray, why the booking can't be changed.
 *  - **edit** — info-blue, "Editing booking {id}" so it's obvious which job is being changed.
 * `sticky top-0` pins it to the top of its scroll container (the aside / slide-in / main column).
 *
 * Padded on BOTH axes: the reason-specific copy is longer than the single message it replaced, so it
 * now wraps on a narrow viewport — and with no horizontal padding it wrapped flush to the screen edge.
 */
export const BookingSheetBanner = ({ mode, jobLabel, notEditableReason, className }: BookingSheetBannerProps) => (
  <div className={clsx("sticky top-0 z-20 px-4 py-2 text-center", mode === "edit" ? "bg-info" : "bg-neutral-silver-gray", className)}>
    <Typography variant="action" className="text-white">
      {mode === "edit"
        ? `Editing booking ${jobLabel ?? ""}`.trim()
        : (notEditableReason && VIEW_COPY[notEditableReason]) || VIEW_FALLBACK}
    </Typography>
  </div>
);
